import React, { useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';

import Field from '~ComponentsImproved/RHF/Field';
import { Box, Button, Input } from '~Styles';
import { useCurrency, useGiftCards, useI18n, useLogger, useSelector } from '~Hooks';
import { GetGiftCards_operativeViewer_giftcards_items as GiftCard } from '~Queries/__queries__';

import { useGiftCardsContext } from './GiftCardsProvider';
import PaymentHandler from './PaymentHandler';
import { useStorage } from '~Components/Storage';

type Inputs = {
  amount: number;
};

type TransactionState =
  | { status: 'IDLE' }
  | { status: 'PENDING' } // Same as loading
  | { status: 'AWAITING_PAYMENT' }
  | { status: 'SUCCESS' }
  | { status: 'ERROR'; error: string; errorType: 'payment' | 'gift_card' };

interface FundsFormProps {
  onSuccess: (shortcode: string) => void;
  giftCard: GiftCard | null;
}

export default function FundsForm({ onSuccess, giftCard }: FundsFormProps) {
  const { i18n } = useI18n();
  const currency = useCurrency();
  const logger = useLogger('gift-card-funds-form');
  const giftCardsContext = useGiftCardsContext();
  const giftCardFunctions = useGiftCards();
  const [transactionState, setTransactionState] = useState<TransactionState>({ status: 'IDLE' });

  const [storage] = useStorage();

  const formMethods = useForm<Inputs>({
    defaultValues: {},
    mode: 'onChange',
  });

  // Generic error handler, logs error and sets form in error state.
  const handleError = (
    errorMessage?: string,
    errorPayload?: any,
    errorType?: 'gift_card' | 'payment',
  ) => {
    logger.error(errorMessage || 'Create gift card: Unkown error', errorPayload);
    setTransactionState((prev) => ({
      status: 'ERROR',
      error: errorMessage || 'Create gift card: Unkown error',
      errorType: errorType || 'gift_card',
    }));
  };

  // Called when payment has gone through (see onSubmit func)
  const requestAddingFunds = () => {
    if (!giftCard) {
      return;
    }

    // If the gift card has no expiration date, we need to activate it first.
    if (giftCard.expiresAt == null) {
      giftCardFunctions
        .activateCard(giftCard.shortcode)
        .then(({ data, errors }) => {
          const wasSuccesful =
            data?.activateGiftcard.success &&
            data?.activateGiftcard.giftcard.activatedAt &&
            data?.activateGiftcard.giftcard.expiresAt;
          if (wasSuccesful) {
            // Everything went as expected, we have now set the expiration date on the giftcard.
            // We can now increase the balance.
            logger.info(`Gift card activated`, { data: data?.activateGiftcard.giftcard });
          } else {
            handleError(errors?.[0].message || 'Activate gift card error');
          }
        })
        .catch((e) => {
          handleError(e.message || 'Activate gift card error');
        });
    }

    setTransactionState({ status: 'PENDING' });
    giftCardFunctions
      .increaseBalance(giftCard?.shortcode, giftCardAmount, true)
      .then(({ data, errors }) => {
        const wasSuccesful = data?.increaseGiftcardBalance.success;

        if (wasSuccesful) {
          setTransactionState({ status: 'SUCCESS' });

          setTimeout(() => {
            onSuccess(giftCard.shortcode);
          }, 500);
        } else {
          handleError(errors?.[0].message || 'Increase gift card balance error');
        }
      })
      .catch((e) => {
        handleError(e.message || 'Increase gift card balance error');
      });
  };

  // We multiply the input amount to get the correct decimal format of: 100,00 => 10000
  const giftCardAmount = formMethods.watch('amount') * 100;

  const onSubmit: SubmitHandler<Inputs> = (values: Inputs) => {
    if (!giftCard) {
      handleError('No gift card was selected for the operation.');
      return;
    }

    setTransactionState({ status: 'AWAITING_PAYMENT' });
  };

  if (!giftCardsContext.item) {
    return (
      <>
        <h2>{i18n('GC.Error.MissingItem')}</h2>
        <Button
          color="colorWhite"
          backgroundColor="colorBlack"
          onTouchStart={giftCardsContext.refetchItemQueries}
          disabled={giftCardsContext.itemLoading}>
          {i18n('General.Retry')}
        </Button>
      </>
    );
  }

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isSubmitting, isValid },
  } = formMethods;

  return (
    <>
      <h1 style={{ marginTop: 0 }}>{i18n('GC.AddFunds.Title')}</h1>
      {/* Step 1 - Fill in details */}
      {transactionState.status === 'IDLE' && (
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
            <Field name="amount">
              <Input
                {...register('amount', {
                  required: i18n('GC.Create.Amount.Required'),
                  min: {
                    value: 5,
                    message: `${i18n('GC.Create.Amount.Minimum')} ${storage.currency.code}`,
                  },
                })}
                fluid
                placeholder={`${i18n('General.AmountIn')} ${storage.currency.code}`}
                type="number"
                error={!!errors.amount}
              />
            </Field>
            <Box
              as="h4"
              fluid
              justifyContent="center"
              marginBottom="small"
              style={{ marginTop: '0' }}>
              ({i18n('GC.Funds.NewBalance')}:{' '}
              {currency.format(Number(giftCard?.balance) + (giftCardAmount ? giftCardAmount : 0))})
            </Box>
            <Box display="flex" justifyContent="flex-end">
              <Button
                fluid
                type="submit"
                backgroundColor="colorBlack"
                color="colorWhite"
                disabled={!isDirty || !isValid || isSubmitting}>
                {i18n('GC.ProceedToPayment')}
              </Button>
            </Box>
          </form>
        </FormProvider>
      )}

      {/* Step 2 - Payment */}
      {transactionState.status === 'AWAITING_PAYMENT' && (
        <PaymentHandler
          amount={giftCardAmount}
          onPaymentError={(message, payload) => {
            handleError(message, payload, 'payment');
          }}
          onPaymentSuccess={() => {
            requestAddingFunds();
          }}
        />
      )}

      {transactionState.status === 'PENDING' && (
        <Box>
          <p>{i18n('General.Loading')}</p>
        </Box>
      )}

      {/* Step 3 - Success! */}
      {transactionState.status === 'SUCCESS' && (
        <Box>
          <p>{i18n('General.Success')}!</p>
        </Box>
      )}

      {/* Step X - Error */}
      {transactionState.status === 'ERROR' && (
        <Box>
          <p>{i18n('General.Error')}!</p>
          {transactionState.errorType === 'gift_card' && (
            <>
              <p>
                The payment went through, but we could not increase the balance.{' '}
                <b>Use the button below to retry the balance increase.</b> (No additional payment
                required).
                <br />
                If this error persists and retrying does not work, please refund the customer and
                contact us.
              </p>
              <Button
                type="button"
                onClick={() => {
                  requestAddingFunds();
                }}>
                {i18n('General.Retry')}
              </Button>
            </>
          )}
        </Box>
      )}
    </>
  );
}
